import { Component, computed, effect, ElementRef, QueryList, signal, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { filter, switchMap, take, timer } from 'rxjs';
import { County } from 'src/app/model/county';
import { Region } from 'src/app/model/region.model';
import { School, Grade } from 'src/app/model/school.model';
import { SchoolDistrict } from 'src/app/model/schoolDistrict.model';
import { AgePipe } from 'src/app/pipes/age.pipe';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { PagingService } from 'src/app/service/paging.service';
import { EnrollmentReportStudent, EnrollmentReportResult } from '../../child-reports/enrollment-report/model/enrollment-report-result.model';
import { EnrollmentReportService } from '../../child-reports/enrollment-report/service/enrollment-report.service';
import { MsgModel } from '../../common-components/msg/model/msg.model';
import { AccessService } from '../../navigation/service/access.service';
import { ServiceDeliveryService } from '../service-delivery/service/service-delivery.service';
import { EnrollmentPeriods } from '../../common-components/child-services/model/enrollment-periods.model';
import { PartyServices } from '../../service-administration/model/party-services.model';
import { EditChildGroupServiceParticipationComponent } from '../edit-child/edit-child-group-service-participation/edit-child-group-service-participation.component';
import { EditChildServiceParticipationModalComponent } from '../edit-child/edit-child-service-participation/edit-child-service-participation-modal/edit-child-service-participation-modal.component';
import { HelperService } from 'src/app/service/helper.service';
import { Route, SearchCriteria, SearchResult } from 'src/app/model/session-tracking/tracking-type';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { createDelayedLoadingSignal } from 'src/app/util/create-delayed-loading-signal';
import { AuthEffects } from '../../auth/state/auth.effects';

@Component({
    selector: 'app-search-group-services',
    templateUrl: './search-group-services.component.html',
    styleUrls: ['./search-group-services.component.scss']
})
export class SearchGroupServicesComponent {
    @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator> = new QueryList<MatPaginator>();
    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();

    @ViewChild('res') resElement: ElementRef;
    @ViewChild('selectedChildren') selectedChildrenElement: ElementRef;

    // Search variables
    selectedSchoolYear: string = '';
    initialSchoolYear: string = '';
    currentSchoolYear: string = '';
    validSchoolYears: string[] = [];
    activeSchoolYearFlag: boolean = true;
    chosenRegions: Region[] = [];
    chosenCounties: County[] = [];
    chosenSchoolDistricts: SchoolDistrict[] = [];
    chosenSchools: School[] = [];

    selectRegionIds: number[] = [];
    selectCountyIds: number[] = [];
    selectSchoolDistrictIds: number[] = [];
    selectSchoolIds: number[] = [];
    selectSchoolYear: string;

    rAttending: boolean = false;
    rNotAttending: boolean = false;

    expiredEligibility: boolean = true;
    expiredQAD: boolean = false;
    notWithdrawnFromSchool: boolean = false;
    missingEndDate: boolean = false;
    k12Ungraded: boolean = false;
    verifiedCoes: boolean = false;

    chosenGrades: Grade[] = [];
    chosenGrade: Grade;

    enrollmentPeriods: EnrollmentPeriods[];
    selectServices: PartyServices[] = [];
    chosenServices: PartyServices[] = [];
    currentChosenServices: PartyServices[] = [];

    chosenServicePeriod: EnrollmentPeriods;

    mismatchServicePeriod: boolean = false;

    // Component variables
    resetField: boolean = false;

    // Table display
    columns = {
        EDIT: { INDEX: 1, ENABLED: true, SORT: 'EDIT', DATA: 'EDIT', DISABLED: false },

        School: { INDEX: 3, ENABLED: true, SORT: 'SCHOOL.NAME', DATA: 'SCHOOL.NAME', DISABLED: false },

        'MSD Number': { INDEX: 6, ENABLED: true, SORT: 'MSD_NUMBER', DATA: 'MSD_NUMBER', DISABLED: true },
        //SSID: { INDEX: 7, ENABLED: true, SORT: 'SSID', DATA: 'SSID', DISABLED: true },

        Name: { INDEX: 10, ENABLED: true, SORT: 'PERSON.LAST_NAME', DATA: 'PERSON.LAST_NAME', DISABLED: true },
        DOB: { INDEX: 11, ENABLED: true, SORT: 'DOB', DATA: 'DOB', DISABLED: true },

        //Gender: { INDEX: 13, ENABLED: true, SORT: 'SEX', DATA: 'SEX', DISABLED: true },

        Grade: { INDEX: 20, ENABLED: true, SORT: 'SCHOOL_ENROLLMENT_DETAILS[0].GRADE', DATA: 'GRADE', DISABLED: true, SCHOOL_ENROLLMENT_DETAILS: true },
        QAD: {
            INDEX: 21,
            ENABLED: true,
            SORT: 'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].CO.QUALIFYING_ARRIVAL_DATE',
            DATA: 'STUDENT_ELIGIBILITIES[0].CO.QUALIFYING_ARRIVAL_DATE',
            DISABLED: true,
            SCHOOL_ENROLLMENT_DETAILS: true
        },
        EoE: {
            INDEX: 22,
            ENABLED: true,
            SORT: 'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE',
            DATA: 'STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE',
            DISABLED: true,
            SCHOOL_ENROLLMENT_DETAILS: true
        },

        'Enroll Date': {
            INDEX: 27,
            ENABLED: true,
            SORT: 'SCHOOL_ENROLLMENT_DETAILS[0].ENROLLMENT_DATE',
            DATA: 'ENROLLMENT_DATE',
            DISABLED: true,
            SCHOOL_ENROLLMENT_DETAILS: true
        },
        'Withdrawal Date': {
            INDEX: 28,
            ENABLED: true,
            SORT: 'SCHOOL_ENROLLMENT_DETAILS[0].WITHDRAWAL_DATE',
            DATA: 'WITHDRAWAL_DATE',
            DISABLED: true,
            SCHOOL_ENROLLMENT_DETAILS: true
        },
        'Departure Date': {
            INDEX: 29,
            ENABLED: true,
            SORT: 'SCHOOL_ENROLLMENT_DETAILS[0].DEPARTURE_DATE',
            DATA: 'DEPARTURE_DATE',
            DISABLED: true,
            SCHOOL_ENROLLMENT_DETAILS: true
        },

        //Track: { INDEX: 30, ENABLED: true, SORT: 'SCHOOL_ENROLLMENT_DETAILS[0].TRACK', DATA: 'TRACK', DISABLED: true, SCHOOL_ENROLLMENT_DETAILS: true },
        'Enroll Type': {
            INDEX: 31,
            ENABLED: true,
            SORT: 'SCHOOL_ENROLLMENT_DETAILS[0].ENROLLMENT_TYPE',
            DATA: 'ENROLLMENT_TYPE',
            DISABLED: true,
            SCHOOL_ENROLLMENT_DETAILS: true
        },
        Services: {
            INDEX: 32,
            ENABLED: true,
            SORT: 'STUDENT_SERVICES[0].START_DATE',
            DATA: 'START_DATE',
            DISABLED: true,
            SCHOOL_ENROLLMENT_DETAILS: false,
            STUDENT_SERVICES: true
        }
    };
    selectedColumns: string[] = [];

    baseDisplayedColumns: string[] = [];
    suffixColumns: string[] = [];
    displayedColumns: string[] = []; // Dynamically populated based on results + baseDisplayedColumns

    // Table results
    tableDataSources: MatTableDataSource<EnrollmentReportStudent>[] = [];
    shoppingCartTableDataSources: MatTableDataSource<EnrollmentReportStudent>[] = [];

    // Results
    results: EnrollmentReportResult[] = [];
    filter: string = '';
    studentLength: number;

    // Shopping Cart
    shoppingCartBackup: any = {};
    shoppingCart: any[] = [];
    shoppingCartFilter: string = '';
    shoppingCartLength: number;

    // Search Children Shopping Cart
    searchChildrenShoppingCart: any[] = [];
    searchChildrenShoppingCartInput: any[] = [];

    // Group Communication variables
    mode: string = '';
    chosenStudent: any;

    isLoading: boolean = false;
    msg: MsgModel = {};
    createMsg: MsgModel = {};

    getPropertyPipe = new GetPropertyPipe();
    getAgePipe = new AgePipe();

    // Permissions
    featuresCheck: string[] = ['Edit Group Student Services', 'Edit Service Deliveries'];
    featurePermissions: any;

    promises: Array<any> = [];

    localStorageReset: any;

    regionLoading = signal(false);
    countyLoading = signal(false);
    districtLoading = signal(false);
    schoolLoading = signal(false);

    backupLoading = signal(false);

    // Immediate combined state
    rawLoading = computed(() => this.backupLoading());
    allLocationLoading = computed(() => this.regionLoading() || this.countyLoading() || this.districtLoading() || this.schoolLoading());

    // UI spinner state
    isBackupLoading = createDelayedLoadingSignal(this.rawLoading, 2000);

    constructor(
        public pagingService: PagingService,
        private dialog: MatDialog,
        private enrollmentReportService: EnrollmentReportService,
        public accessService: AccessService,
        private serviceDeliveryService: ServiceDeliveryService,
        private helperService: HelperService,
        private authEffects: AuthEffects,
        private router: Router
    ) {
        effect(
            () => {
                const anyLoading = this.allLocationLoading();

                // only turn it off when everything finished
                if (!anyLoading && this.backupLoading()) {
                    this.backupLoading.set(false);
                }
            },
            { allowSignalWrites: true }
        );
    }

    ngOnInit(): void {
        this.isLoading = true;

        // Get localStorageReset
        try {
            this.localStorageReset = JSON.parse(localStorage.getItem('reset') || '{}');
        } catch (e) {
            this.localStorageReset = {};
        }

        // Permissions
        this.getFeaturePermissions();

        // Reference Data
        this.getEnrollmentPeriods();

        // Get Shopping Cart
        this.getBackup();

        // Display columns
        let prefixColumns: string[] = [];
        this.displayedColumns = this.generateDisplayColumns(prefixColumns);

        if (!this.localStorageReset[this.router.url]) {
            Promise.all(this.promises).then((res) => {
                setTimeout(() => {
                    if (!!this.shoppingCartBackup.SearchResults) {
                        this.backupLoading.set(true);
                        setTimeout(() => {
                            this.restoreSearchCriteriaFromBackup();
                        }, 200);
                    }
                }, 1000);
                this.isLoading = false;
            });
        } else {
            this.isLoading = false;
        }
    }

    getFeaturePermissions() {
        this.accessService.getFeatureAccessPromise(this.featuresCheck).then((res) => {
            this.featurePermissions = res;
        });
    }

    getEnrollmentPeriods() {
        let deferred = new Promise((resolve, reject) => {
            this.serviceDeliveryService.getEnrollmentPeriods().subscribe((res) => {
                if (res.result) {
                    this.enrollmentPeriods = res.result.filter(
                        (ep: EnrollmentPeriods) => ep.PERIOD === 'Regular Year' || ep.PERIOD === 'Summer' || ep.PERIOD === 'Intersession'
                    );
                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    search(includeSearchResult: Boolean) {
        if (this.checkValid() && (this.rAttending || this.rNotAttending)) {
            this.isLoading = true;
            this.msg = {};
            this.filter = '';

            this.enrollmentReportService.searchStudentsBySchool(this.generatePayload(includeSearchResult)).subscribe({
                next: (res) => {
                    if (res.students) {
                        this.shoppingCartRemoveAll(false);

                        res.students.forEach((student: any) => {
                            // Attach AGE
                            student.AGE = parseInt(this.getAgePipe.transform(student.DOB).replace(/[{()}]/g, ''));
                        });

                        this.results = res.students.sort((n1: EnrollmentReportResult, n2: EnrollmentReportResult) => (n1.NAME! > n2.NAME! ? 1 : -1));
                        this.studentLength = res.students.length;
                    } else if (res.msg) {
                        this.results = [];
                        this.msg = res.msg;
                        this.isLoading = false;
                    } else {
                        this.results = [];
                        this.isLoading = false;
                        this.msg = { SUCCESS: false, MSG: 'Error retrieving Service Report.' };
                    }
                },
                error: (err) => {
                    this.results = [];
                    this.msg = { SUCCESS: false, MSG: 'Error retrieving Service Report.' };
                    this.isLoading = false;
                },
                complete: () => {
                    // Display current performance period
                    this.currentSchoolYear = this.selectedSchoolYear;

                    // Updates the Tables to be displayed w/ this.results
                    if (this.results.length > 0) {
                        this.updateDataSource(true);
                        this.updateShoppingCartDataSource();
                    }

                    // Set current selected service
                    this.currentChosenServices = JSON.parse(JSON.stringify(this.chosenServices));

                    // Check if searched school year is an active year
                    let validSchoolYear = this.validSchoolYears.find((sy: string) => sy === this.currentSchoolYear);

                    if (validSchoolYear) {
                        this.activeSchoolYearFlag = true;
                    } else {
                        this.activeSchoolYearFlag = false;
                    }

                    // Save shopping cart
                    this.restoreShoppingCart();
                    this.saveBackup();

                    // Display results
                    this.isLoading = false;

                    setTimeout(() => {
                        const targetElement = this.resElement.nativeElement;
                        targetElement.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                }
            });
        } else {
            this.msg = { SUCCESS: false, MSG: 'Please select a School District and an Enrollment Type.' };
        }
    }

    generatePayload(includeSearchResult: Boolean): any {
        let payload: any = {};

        payload.chosenRegions = this.chosenRegions.map((obj) => obj.ID);
        payload.chosenCounties = this.chosenCounties.map((obj) => obj.ID);
        payload.chosenSchoolDistricts = this.chosenSchoolDistricts.map((obj) => obj.ID);
        payload.chosenSchools = this.chosenSchools.map((obj) => obj.ID);
        payload.schoolYear = this.selectedSchoolYear;

        payload.rAttending = this.rAttending;
        payload.rNotAttending = this.rNotAttending;

        payload.expiredEligibility = this.expiredEligibility;
        payload.expiredQAD = this.expiredQAD;
        payload.notWithdrawnFromSchool = this.notWithdrawnFromSchool;
        payload.missingEndDate = this.missingEndDate;

        payload.chosenGrade = this.chosenGrade;

        if (this.shoppingCartBackup.SearchResults && this.shoppingCartBackup.SearchResults.length > 0 && includeSearchResult) {
            payload.studentIDs = this.shoppingCartBackup.SearchResults.map((s: any) => s.ID);
        }

        if (this.chosenServices && this.chosenServices.length > 0) {
            payload.selectedService = this.chosenServices[0].ID;
        }
        if (this.chosenServicePeriod) {
            payload.servicePeriod = this.chosenServicePeriod.ID;
        }

        payload.showServicesColumn = true;

        return payload;
    }

    generateDisplayColumns(prefixColumns?: string[]) {
        let displayedColumns: string[] = prefixColumns ? prefixColumns : [];
        let columns: any = this.columns;
        //displayedColumns = displayedColumns.concat(this.baseDisplayedColumns);

        const properties = Object.keys(columns);
        properties.forEach((property) => {
            if (columns[property].ENABLED) {
                displayedColumns.push(columns[property].SORT);
            }
        });

        return displayedColumns;
    }

    updateDataSource(newSort?: boolean) {
        setTimeout(() => {
            this.tableDataSources = [];

            // Data
            let dataSource = new MatTableDataSource<any>();
            dataSource.data = this.results.filter((r) => !this.shoppingCart.map((sc) => sc.ID).includes(r.ID));

            // Paginator
            dataSource.paginator = this.paginators.toArray()[0];

            // Sort
            dataSource.sort = this.sorts.toArray()[0];
            dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);
            if (newSort) {
                const defaultSort: Sort = { active: 'MSD_NUMBER', direction: 'asc' };
                dataSource.sort.active = defaultSort.active;
                dataSource.sort.direction = defaultSort.direction;
                dataSource.sort.sortChange.emit(defaultSort);
            } else {
                dataSource.sort.sortChange.emit(this.sorts.toArray()[0]);
            }
            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                const dataStr = JSON.stringify(data).toLowerCase();
                return dataStr.indexOf(filter) != -1;
            };

            this.tableDataSources.push(dataSource);
        }, 100);
    }

    setFilter() {
        this.tableDataSources.forEach((dataSource, index) => {
            dataSource.filter = this.filter.trim().toLocaleLowerCase();
        });
    }

    checkValid() {
        if (
            this.chosenRegions.length > 0 &&
            this.chosenCounties.length > 0 &&
            this.chosenSchoolDistricts.length > 0 &&
            this.chosenSchools.length > 0 &&
            this.selectedSchoolYear &&
            (this.rAttending || this.rNotAttending)
        ) {
            return true;
        }

        return false;
    }

    toNumberArray(array: any[]): number[] {
        let returnArray: number[] = [];

        array.forEach((obj) => {
            obj.ID ? returnArray.push(obj.ID) : null;
        });

        return returnArray;
    }

    performancePeriodEvent(performancePeriod: string) {
        this.selectedSchoolYear = performancePeriod;
    }

    // Filter Emiiters
    gradeEventEmitter(grades: Grade[]) {
        if (grades) {
            if (Array.isArray(grades)) {
                this.chosenGrades = grades;
                this.chosenGrade = grades[0];
            } else {
                this.chosenGrades = [grades];
                this.chosenGrade = grades;
            }
        }
    }

    populateSchoolYearsEventEmitter(schoolYears: string[]) {
        if (schoolYears) {
            this.validSchoolYears = JSON.parse(JSON.stringify(schoolYears));
            this.validSchoolYears = this.validSchoolYears.splice(0, 3);
        }
    }

    // MISC
    reset() {
        this.resetField = !this.resetField;
        //This is optional code To reset the value after
        const s = timer(1000);
        s.subscribe((val) => {
            this.resetField = !this.resetField;
        });

        this.chosenRegions = [];
        this.chosenCounties = [];
        this.chosenSchoolDistricts = [];
        this.chosenSchools = [];

        this.rAttending = false;
        this.rNotAttending = false;
        this.expiredEligibility = true;
        this.expiredQAD = false;
        this.notWithdrawnFromSchool = false;
        this.missingEndDate = false;
        this.k12Ungraded = false;
        this.verifiedCoes = false;

        this.chosenServices = [];
        this.chosenServicePeriod = new EnrollmentPeriods();

        this.msg = {};

        this.deSelectAllColumns(this.columns);

        this.localStorageReset[this.router.url] = true;
        localStorage.setItem('reset', JSON.stringify(this.localStorageReset));
    }

    announceSortChange(sortState: Sort) {}

    checkbox(properties: string[], obj: any) {
        let columns: any = obj;
        let columnKeys = Object.keys(columns);

        columnKeys = columnKeys.filter((key) => !obj[key].DISABLED);

        columnKeys.forEach((filter: string) => {
            if (properties.indexOf(filter) >= 0) {
                columns[filter].ENABLED = true;
            } else {
                columns[filter].ENABLED = false;
            }
        });
    }

    selectAllColumns(obj: any) {
        let columnKeys = Object.keys(obj);

        columnKeys = columnKeys.filter((key) => !obj[key].DISABLED);

        this.checkbox(columnKeys, obj);

        this.selectedColumns = columnKeys;
    }

    deSelectAllColumns(obj: any) {
        this.checkbox([], obj);

        this.selectedColumns = [];
    }

    selectionCount(obj: any, key: string) {
        let count: number = 0;

        if (obj[key]) {
            if (obj[key].OPTIONS) {
                obj[key].OPTIONS.forEach((option: any) => {
                    if (option.ENABLED) {
                        count += 1;
                    }
                });
            }
        }

        return count;
    }

    searchStudentChange(students: any[]) {
        this.results = students;

        // Updates the Tables to be displayed w/ this.results
        if (this.results.length > 0) {
            this.updateDataSource();
        }
    }
    getservicePeriod(id: number) {
        let chosenServicePeriod = this.enrollmentPeriods.find((ep) => ep.ID === id);
        if (chosenServicePeriod) {
            this.chosenServicePeriod = chosenServicePeriod;
        }
    }
    servicePeriodChange(event: any) {
        // let chosenServicePeriod = this.enrollmentPeriods.find((ep) => ep.PERIOD === event.value);
        // if (chosenServicePeriod) {
        //     this.chosenServicePeriod = chosenServicePeriod!;
        // }
    }

    serviceEventEmitter(event: any) {
        if (Array.isArray(event)) {
            this.chosenServices = event;
        } else {
            this.chosenServices = [event];
        }
    }

    addChild(child: any) {}

    mergeArrays(targetArray: any[], mergingArray: any[], propertiesToMerge: string[]) {
        if (propertiesToMerge) {
            mergingArray.forEach((obj) => {
                for (let propertyName in obj) {
                    if (
                        !propertiesToMerge.find((property) => {
                            return property === propertyName;
                        })
                    ) {
                        delete obj[propertyName];
                    }
                }
            });
        }

        let result: any[] = targetArray.map((obj) => {
            return Object.assign(
                obj,
                mergingArray.find((ma) => ma.ID === obj.ID)
            );
        });

        return result;
    }

    // Shopping Cart functions
    shoppingCartAdd(obj: any) {
        this.shoppingCart.push(obj);

        this.updateDataSource();
        this.updateShoppingCartDataSource();

        this.saveBackup();
    }

    shoppingCartAddAll() {
        this.results.forEach((obj) => {
            let shoppingCartIndex = this.shoppingCart.findIndex((sc) => sc.ID === obj.ID);

            if (shoppingCartIndex === -1) {
                this.shoppingCart.push(obj);
            }
        });

        this.updateDataSource();
        this.updateShoppingCartDataSource();

        this.saveBackup();

        setTimeout(() => {
            const targetElement = this.selectedChildrenElement.nativeElement;
            targetElement.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }

    shoppingCartRemove(obj: any) {
        let shoppingCartIndex = this.shoppingCart.findIndex((sc) => sc.ID === obj.ID);

        if (shoppingCartIndex >= 0) {
            this.shoppingCart.splice(shoppingCartIndex, 1);
        }

        // Search Children removal
        if (!obj.SCHOOL) {
            let searchChildrenShoppingCartIndex = this.searchChildrenShoppingCart.findIndex((sc) => sc.ID === obj.ID);

            if (searchChildrenShoppingCartIndex >= 0) {
                this.searchChildrenShoppingCart.splice(searchChildrenShoppingCartIndex, 1);

                this.searchChildrenShoppingCartInput = JSON.parse(JSON.stringify(this.searchChildrenShoppingCart));
            }
        }

        this.updateDataSource();
        this.updateShoppingCartDataSource();

        this.saveBackup();
    }

    shoppingCartRemoveAll(saveBackup: boolean = true) {
        this.shoppingCart = [];
        this.searchChildrenShoppingCart = [];
        this.searchChildrenShoppingCartInput = [];

        this.updateDataSource();
        this.updateShoppingCartDataSource();

        if (saveBackup) {
            this.saveBackup();
        }
    }

    updateShoppingCartDataSource() {
        setTimeout(() => {
            this.shoppingCartTableDataSources = [];

            // Data
            let dataSource = new MatTableDataSource<any>();
            //dataSource.data = this.shoppingCart;
            dataSource.data = [...new Set([...this.shoppingCart, ...this.searchChildrenShoppingCart])];

            // Paginator
            dataSource.paginator = this.paginators.toArray()[1];

            // Sort
            dataSource.sort = this.sorts.toArray()[1];
            dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);
            if (this.shoppingCart.length === 0) {
                const defaultSort: Sort = { active: 'MSD_NUMBER', direction: 'asc' };
                dataSource.sort.active = defaultSort.active;
                dataSource.sort.direction = defaultSort.direction;
                dataSource.sort.sortChange.emit(defaultSort);
            } else {
                dataSource.sort.sortChange.emit(this.sorts.toArray()[1]);
            }
            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                const dataStr = JSON.stringify(data).toLowerCase();
                return dataStr.indexOf(filter) != -1;
            };

            this.shoppingCartTableDataSources.push(dataSource);
            this.shoppingCartLength = dataSource.data.length;

            this.setMismatchEnrollmentType();
        }, 100);
    }

    setShoppingCartFilter() {
        this.shoppingCartTableDataSources.forEach((dataSource, index) => {
            dataSource.filter = this.shoppingCartFilter.trim().toLocaleLowerCase();
        });
    }

    setMismatchEnrollmentType() {
        const uniqueEnrollmentTypes = [
            ...new Set(this.shoppingCartTableDataSources[0].data.map((item) => (item.STUDENT_SERVICES ? item.STUDENT_SERVICES[0].ENROLLMENT_PERIODS_ID : 0)))
        ];
        if (uniqueEnrollmentTypes.length > 1) {
            this.mismatchServicePeriod = true;
        } else {
            this.mismatchServicePeriod = false;
        }
    }

    // Backup functions
    getBackup() {
        let deferred = new Promise((resolve, reject) => {
            this.authEffects.isLoggedInNavigated$
                .pipe(
                    filter(Boolean), // only proceed when true
                    take(1), // run once per component lifecycle (or remove if you want per-login)
                    switchMap(() => this.helperService.currSessionTracking$.pipe(take(1)))
                )
                .subscribe((curSessionTrack) => {
                    let searchRoute = curSessionTrack.routes
                        ?.slice()
                        .reverse()
                        .find((x) => x.path == this.router.url && x.searchCriterias.length > 0);

                    if (searchRoute != null) {
                        let workingShoppingCart: any = {
                            SearchCriterias: {},
                            SearchResults: [],
                            ChosenResults: []
                        };

                        workingShoppingCart.SearchCriterias = ['GroupService']
                            .map((screen) =>
                                curSessionTrack.routes
                                    ?.slice()
                                    .reverse()
                                    .flatMap((route) => route.searchCriterias ?? [])
                                    .find((sc) => sc.screenName === screen)
                            )
                            .filter((item): item is NonNullable<typeof item> => Boolean(item));

                        workingShoppingCart.SearchResults = searchRoute.searchResults;
                        workingShoppingCart.ChosenResults = searchRoute.chosenResults;

                        this.shoppingCartBackup = workingShoppingCart;
                    }

                    resolve(true);
                });
        });
        this.promises.push(deferred);
    }

    async restoreSearchCriteriaFromBackup() {
        // GroupService
        let searchCriteriaTab1 = this.shoppingCartBackup?.SearchCriterias.find((sc: SearchCriteria) => sc.screenName === 'GroupService');
        if (searchCriteriaTab1?.payLoad) {
            let payloadTab1 = JSON.parse(searchCriteriaTab1?.payLoad);
            //console.warn(payloadTab1);
            let searchResultsTab1 = searchCriteriaTab1?.results;
            //console.warn(searchResultsTab1);
            let chosenResultsTab1 = searchCriteriaTab1?.results;
            //console.warn(chosenResultsTab1);

            this.chosenRegions = payloadTab1?.chosenRegions?.map((id: number) => ({ ID: id })) ?? [];

            setTimeout(() => {
                this.chosenCounties = payloadTab1?.chosenCounties?.map((id: number) => ({ ID: id })) ?? [];
                setTimeout(() => {
                    this.chosenSchoolDistricts = payloadTab1?.chosenSchoolDistricts?.map((id: number) => ({ ID: id })) ?? [];
                    setTimeout(() => {
                        this.chosenSchools = payloadTab1?.chosenSchools?.map((id: number) => ({ ID: id })) ?? [];

                        this.saveBackup();
                    }, 3);
                }, 2);
            }, 1);

            if (payloadTab1?.schoolYear) {
                this.selectSchoolYear = payloadTab1.schoolYear;
            }

            this.rAttending = !!payloadTab1.rAttending;
            this.rNotAttending = !!payloadTab1.rNotAttending;
            this.expiredEligibility = !!payloadTab1.expiredEligibility;
            this.expiredQAD = !!payloadTab1.expiredQAD;
            this.notWithdrawnFromSchool = !!payloadTab1.notWithdrawnFromSchool;
            this.missingEndDate = !!payloadTab1.missingEndDate;

            if (payloadTab1?.chosenGrade) {
                this.chosenGrades = [payloadTab1.chosenGrade];
            }

            if (payloadTab1?.chosenServices) {
                this.selectServices = payloadTab1.chosenServices;
                //console.warn(this.selectServices);
            }

            if (payloadTab1?.chosenServicePeriod) {
                this.getservicePeriod(payloadTab1.chosenServicePeriod);
            }
        }
    }

    // restoreSearchCriteriaFromBackup() {
    //     if (this.shoppingCartBackup.SearchCriterias) {
    //         this.selectRegionIds = this.shoppingCartBackup.SearchCriterias.chosenRegions;
    //         this.selectCountyIds = this.shoppingCartBackup.SearchCriterias.chosenCounties;
    //         this.selectSchoolDistrictIds = this.shoppingCartBackup.SearchCriterias.chosenSchoolDistricts;
    //         this.selectSchoolIds = this.shoppingCartBackup.SearchCriterias.chosenSchools;

    //         this.selectSchoolYear = this.shoppingCartBackup.SearchCriterias.schoolYear;

    //         this.rAttending = this.shoppingCartBackup.SearchCriterias.rAttending;
    //         this.rNotAttending = this.shoppingCartBackup.SearchCriterias.rNotAttending;

    //         this.expiredEligibility = this.shoppingCartBackup.SearchCriterias.expiredEligibility;
    //         this.expiredQAD = this.shoppingCartBackup.SearchCriterias.expiredQAD;
    //         this.notWithdrawnFromSchool = this.shoppingCartBackup.SearchCriterias.notWithdrawnFromSchool;
    //         this.missingEndDate = this.shoppingCartBackup.SearchCriterias.missingEndDate;

    //         if (this.shoppingCartBackup.SearchCriterias.chosenGrade) {
    //             this.chosenGrades = [this.shoppingCartBackup.SearchCriterias.chosenGrade];
    //         }

    //         if (this.shoppingCartBackup.SearchCriterias.chosenServices) {
    //             this.chosenServices = this.shoppingCartBackup.SearchCriterias.chosenServices;
    //         }
    //         if (this.shoppingCartBackup.SearchCriterias.chosenServicePeriod) {
    //             this.getservicePeriod(this.shoppingCartBackup.SearchCriterias.chosenServicePeriod);
    //         }
    //     }
    // }

    restoreShoppingCart() {
        if (this.shoppingCartBackup.ChosenResults && this.shoppingCartBackup.ChosenResults.length > 0) {
            this.shoppingCart = [];

            //let chosenResults = this.shoppingCartBackup.ChosenResults.map((cr: any) => cr.ID);
            let chosenResults = this.shoppingCartBackup.ChosenResults;
            this.shoppingCart = this.results.filter((r) => chosenResults.includes(r.ID));

            this.updateDataSource();
            this.updateShoppingCartDataSource();

            this.shoppingCartBackup.ChosenResults = [];
        }
    }

    async saveBackup() {
        let shoppingCartBackup: any = {};
        shoppingCartBackup.CartId = 'GroupServices';
        shoppingCartBackup.SearchCriterias = this.generateSearchCriteriasBackup();
        shoppingCartBackup.SearchResults = this.generateResultsBackup(this.results);
        shoppingCartBackup.ChosenResults = this.generateChosenResultsBackup(this.shoppingCart);

        const route = {} as Route;
        route.path = this.router.url;

        route.timeStamp = new Date().toLocaleString();
        route.bLastVisited = false;
        route.searchCriterias = [];
        route.searchResults = [];
        route.chosenResults = [];

        const searchCriteria = {} as SearchCriteria;
        searchCriteria.screenName = 'GroupService';
        searchCriteria.payLoad = JSON.stringify(shoppingCartBackup.SearchCriterias.payLoad);
        route.searchCriterias.push(searchCriteria);

        const searchResult = {} as SearchResult;
        searchResult.screenName = 'GroupService';
        searchResult.results = shoppingCartBackup.SearchResults;
        route.searchResults.push(searchResult);

        const chosenResult = {} as SearchResult;
        chosenResult.screenName = 'GroupService';
        chosenResult.results = shoppingCartBackup.ChosenResults;
        route.chosenResults.push(chosenResult);

        this.helperService.addCurrSessionTrackingRoute(route);

        this.localStorageReset[this.router.url] = false;
        localStorage.setItem('reset', JSON.stringify(this.localStorageReset));
    }

    generateSearchCriteriasBackup() {
        let searchCriterias: any = {};
        searchCriterias.screenName = 'GroupService';
        searchCriterias.payLoad = {};
        searchCriterias.payLoad.chosenRegions = this.chosenRegions.map((obj) => obj.ID);
        searchCriterias.payLoad.chosenCounties = this.chosenCounties.map((obj) => obj.ID);
        searchCriterias.payLoad.chosenSchoolDistricts = this.chosenSchoolDistricts.map((obj) => obj.ID);
        searchCriterias.payLoad.chosenSchools = this.chosenSchools.map((obj) => obj.ID);
        searchCriterias.payLoad.schoolYear = this.selectedSchoolYear;

        searchCriterias.payLoad.rAttending = this.rAttending;
        searchCriterias.payLoad.rNotAttending = this.rNotAttending;

        searchCriterias.payLoad.expiredEligibility = this.expiredEligibility;
        searchCriterias.payLoad.expiredQAD = this.expiredQAD;
        searchCriterias.payLoad.notWithdrawnFromSchool = this.notWithdrawnFromSchool;
        searchCriterias.payLoad.missingEndDate = this.missingEndDate;

        if (this.chosenGrade) {
            searchCriterias.payLoad.chosenGrade = { ID: this.chosenGrade.ID };
        }
        if (this.chosenServices) {
            searchCriterias.payLoad.chosenServices = this.chosenServices;
        }
        if (this.chosenServicePeriod) searchCriterias.payLoad.chosenServicePeriod = this.chosenServicePeriod.ID;

        return searchCriterias;
    }

    generateResultsBackup(results: any[]) {
        return results.map((r) => {
            return { ID: r.ID };
        });
    }

    generateChosenResultsBackup(shoppingCart: any[]) {
        return shoppingCart.map((sc) => {
            return { ID: sc.ID };
        });
    }

    searchChildrenShoppingCartEvent(event: any) {
        this.searchChildrenShoppingCart = event;

        this.updateShoppingCartDataSource();
    }

    /*
		Service Dialog Functions
	*/
    groupAddDialog() {
        let payload: any = {};
        payload.students = this.shoppingCart;
        payload.mode = '';
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.data = payload;
        dialogConfig.maxHeight = '99vh';
        dialogConfig.minWidth = '80vw';
        const dialogRef = this.dialog.open(EditChildServiceParticipationModalComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((res: any) => {
            if (res) {
                if (res.refresh) {
                    this.search(true);
                    this.shoppingCartRemoveAll();
                }
            }
        });
    }

    groupUpdateDialog() {
        let payload: any = {};
        payload.students = this.shoppingCart;
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.data = payload;
        dialogConfig.maxHeight = '99vh';
        dialogConfig.minWidth = '80vw';
        const dialogRef = this.dialog.open(EditChildGroupServiceParticipationComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((res: any) => {
            if (res) {
                if (res.refresh) {
                    this.search(true);
                    this.shoppingCartRemoveAll();
                }
            }
        });
    }
}
